/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef, useMemo } from "react";
import { Skeleton } from "primereact/skeleton";
import { Menu } from "primereact/menu";
import { confirmDialog } from "primereact/confirmdialog";
import {
  accessAPI,
  Boton,
  Titulo,
  Formulario,
  Sidebar
} from "../../../../shared";
import DiagramaFlujo from "./DiagramaFlujo";

// Valores del selector para los dos finales de la actividad. En la API los dos
// son destino null; el segundo lleva además sinCumplimiento.
const FIN = "fin";
const FIN_SIN_CUMPLIMIENTO = "fin_sin_cumplimiento";
const esFinal = (destino) =>
  destino === FIN || destino === FIN_SIN_CUMPLIMIENTO;

/**
 * Pasos y flujo de un tipo de actividad, en una sola card con el flujograma.
 *
 * Cada paso es una caja con sus datos; su menú abre un sidebar para editar su
 * configuración, a dónde va si se aprueba o se rechaza (con más de un destino,
 * esos pasos se abren en paralelo) y si es el primer paso de la actividad. Un
 * paso puede terminar la actividad en cualquiera de sus dos finales: el fin, o
 * el fin sin cumplimiento.
 * Desde el título se agregan pasos.
 *
 * Nada de eso se guarda en el momento: los cambios se aplican al flujograma y
 * quedan pendientes hasta "Guardar cambios", que guarda pasos y flujo en una
 * sola operación. Si el flujo resultante no es válido la API no guarda nada,
 * así un tipo de actividad nunca queda con el flujo incompleto.
 *
 * Dibuja su propio título; el contenedor lo pone el que lo monta.
 *
 * @param {() => void} onGuardado se llama después de guardar
 */
export default function FlujoPasos({ actividadTipoId, onGuardado, toast }) {
  // Lo guardado, como lo devuelve la API
  const [flujo, setFlujo] = useState(null);
  // Borrador: los pasos como se están editando. Cada paso se identifica por
  // `ref` (su id si ya existe, o una clave provisoria si es nuevo) y lleva sus
  // destinos en `aprueba` y `rechaza` (refs de otros pasos, o FIN).
  const [pasos, setPasos] = useState([]);
  const [inicialRef, setInicialRef] = useState(null);
  const [hayCambios, setHayCambios] = useState(false);
  const [guardando, setGuardando] = useState(false);

  // Catálogos para el formulario y para mostrar los pasos nuevos
  const [tiposDeColaborador, setTiposDeColaborador] = useState([]);
  const [tiposDePaso, setTiposDePaso] = useState([]);

  // Sidebar de un paso (nuevo o existente)
  const [pasoEditando, setPasoEditando] = useState(null);
  const [formKey, setFormKey] = useState(0);

  // Menú de un paso del flujograma
  const [pasoDelMenu, setPasoDelMenu] = useState(null);
  const menuRef = useRef(null);
  const proximaClave = useRef(1);

  function mostrarError(respuesta, porDefecto, titulo = "Error") {
    toast.current?.show({
      severity: "error",
      summary: titulo,
      // La API devuelve un mensaje por cada problema del flujo
      detail: Array.isArray(respuesta)
        ? respuesta.map((error) => error.msg).join(" ")
        : porDefecto,
      sticky: true
    });
  }

  // Arma el borrador a partir de lo guardado
  function recibir(respuesta) {
    const destinos = (pasoId, resultado) =>
      respuesta.transiciones
        .filter((t) => t.origenId === pasoId && t.resultado === resultado)
        .map((t) =>
          t.destinoId !== null
            ? String(t.destinoId)
            : t.sinCumplimiento
              ? FIN_SIN_CUMPLIMIENTO
              : FIN
        );
    setPasos(
      respuesta.pasos.map((paso) => ({
        ref: String(paso.id),
        id: paso.id,
        nombre: paso.nombre,
        auditortipodecisorid: paso.auditortipodecisor?.id ?? null,
        actividadPasoTipoId: paso.actividadPasoTipo?.id ?? null,
        aceptaAdministrador: paso.aceptaAdministrador,
        aprueba: destinos(paso.id, "APROBADO"),
        rechaza: destinos(paso.id, "RECHAZADO")
      }))
    );
    setInicialRef(
      respuesta.pasoInicialId ? String(respuesta.pasoInicialId) : null
    );
    setHayCambios(false);
    setFlujo(respuesta);
  }

  useEffect(() => {
    accessAPI(
      "GET",
      `admin/actividad/actividadtipo/${actividadTipoId}/flujo`,
      null,
      recibir,
      (respuesta) => mostrarError(respuesta, "Error al cargar el flujo")
    );
    accessAPI(
      "GET",
      "admin/auditor/auditorTipo?dropdown=true",
      null,
      (respuesta) => setTiposDeColaborador(respuesta),
      () => {}
    );
    accessAPI(
      "GET",
      "admin/actividad/pasotipo?dropdown=true",
      null,
      (respuesta) => setTiposDePaso(respuesta),
      () => {}
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actividadTipoId]);

  const etiquetaDe = (opciones, valor) =>
    opciones.find((opcion) => opcion.value === valor)?.etiqueta || "";

  // Para el flujograma cada paso necesita un id numérico: el suyo, o uno
  // negativo si todavía no está guardado.
  const idDiagrama = useMemo(
    () =>
      new Map(pasos.map((paso, i) => [paso.ref, paso.id ?? -(i + 1)])),
    [pasos]
  );
  const pasosDelDiagrama = pasos.map((paso) => ({
    id: idDiagrama.get(paso.ref),
    ref: paso.ref,
    nombre: paso.nombre,
    tipo: etiquetaDe(tiposDePaso, paso.actividadPasoTipoId),
    decisor: etiquetaDe(tiposDeColaborador, paso.auditortipodecisorid),
    aceptaAdministrador: paso.aceptaAdministrador
  }));
  const transicionesDelDiagrama = pasos.flatMap((paso) =>
    [
      ["APROBADO", paso.aprueba],
      ["RECHAZADO", paso.rechaza]
    ].flatMap(([resultado, destinos]) =>
      destinos.map((destino) => ({
        origenId: idDiagrama.get(paso.ref),
        resultado,
        destinoId: esFinal(destino) ? null : idDiagrama.get(destino),
        sinCumplimiento: destino === FIN_SIN_CUMPLIMIENTO
      }))
    )
  );

  function abrirNuevoPaso() {
    setPasoEditando({
      ref: `n${proximaClave.current++}`,
      id: null,
      esNuevo: true,
      nombre: "",
      auditortipodecisorid: null,
      actividadPasoTipoId: null,
      aceptaAdministrador: false,
      aprueba: [],
      rechaza: []
    });
    setFormKey((k) => k + 1);
  }

  function abrirEditarPaso(paso) {
    setPasoEditando(paso);
    setFormKey((k) => k + 1);
  }

  // Aplica al borrador lo que se aceptó en el sidebar. No guarda nada.
  function aceptarPaso(campos) {
    const { ref } = pasoEditando;
    const actualizado = {
      ref,
      id: pasoEditando.id,
      nombre: campos.nombre,
      auditortipodecisorid: campos.auditortipodecisorid,
      actividadPasoTipoId: campos.actividadPasoTipoId,
      aceptaAdministrador: campos.aceptaAdministrador === true,
      aprueba: campos.aprueba || [],
      rechaza: campos.rechaza || []
    };
    setPasos((prev) =>
      pasoEditando.esNuevo
        ? [...prev, actualizado]
        : prev.map((paso) => (paso.ref === ref ? actualizado : paso))
    );
    // El check marca a este paso como el primero; desmarcarlo deja al flujo
    // sin paso inicial hasta que se marque otro.
    if (campos.inicial === true) {
      setInicialRef(ref);
    } else if (inicialRef === ref) {
      setInicialRef(null);
    }
    setHayCambios(true);
    setPasoEditando(null);
  }

  // Saca el paso del borrador. Lo que iba a él queda desconectado: nada se
  // reconecta solo.
  function eliminarPaso(paso) {
    setPasos((prev) =>
      prev
        .filter((otro) => otro.ref !== paso.ref)
        .map((otro) => ({
          ...otro,
          aprueba: otro.aprueba.filter((destino) => destino !== paso.ref),
          rechaza: otro.rechaza.filter((destino) => destino !== paso.ref)
        }))
    );
    if (inicialRef === paso.ref) setInicialRef(null);
    setHayCambios(true);
  }

  function guardar() {
    setGuardando(true);
    accessAPI(
      "PUT",
      `admin/actividad/actividadtipo/${actividadTipoId}/flujo`,
      {
        pasos: pasos.map((paso) => ({
          ref: paso.ref,
          id: paso.id,
          nombre: paso.nombre,
          auditortipodecisorid: paso.auditortipodecisorid,
          actividadPasoTipoId: paso.actividadPasoTipoId,
          aceptaAdministrador: paso.aceptaAdministrador,
          inicial: paso.ref === inicialRef
        })),
        transiciones: pasos.flatMap((paso) =>
          [
            ["APROBADO", paso.aprueba],
            ["RECHAZADO", paso.rechaza]
          ].flatMap(([resultado, destinos]) =>
            destinos.map((destino) => ({
              origen: paso.ref,
              resultado,
              destino: esFinal(destino) ? null : destino,
              sinCumplimiento: destino === FIN_SIN_CUMPLIMIENTO
            }))
          )
        )
      },
      (respuesta) => {
        setGuardando(false);
        recibir(respuesta);
        onGuardado?.();
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: "Pasos y flujo guardados"
        });
      },
      (respuesta) => {
        setGuardando(false);
        mostrarError(respuesta, "Error al guardar el flujo", "No se guardó");
      }
    );
  }

  // Formulario del sidebar: configuración del paso + a dónde va + si es el
  // primero. Los cambios rigen para las actividades nuevas: las que ya
  // existen tienen sus propios pasos y su propio flujo.
  const configDelPaso = useMemo(() => {
    if (!pasoEditando) return null;
    const destinos = [
      ...pasos
        .filter((paso) => paso.ref !== pasoEditando.ref)
        .map((paso) => ({ value: paso.ref, etiqueta: paso.nombre })),
      { value: pasoEditando.ref, etiqueta: "Se queda en este paso" },
      { value: FIN, etiqueta: "Fin de la actividad" },
      { value: FIN_SIN_CUMPLIMIENTO, etiqueta: "Fin sin cumplimiento" }
    ];
    return {
      secciones: [
        {
          campos: [
            {
              nombre: "nombre",
              tipoitemformularioid: 1,
              etiqueta: "Nombre",
              requerido: true,
              respuesta: pasoEditando.nombre
            },
            {
              nombre: "auditortipodecisorid",
              tipoitemformularioid: 5,
              etiqueta: "Tipo de colaborador decisor",
              requerido: true,
              opciones: tiposDeColaborador,
              sinOpcionInicial: true,
              respuesta: pasoEditando.auditortipodecisorid
            },
            {
              nombre: "actividadPasoTipoId",
              tipoitemformularioid: 5,
              etiqueta: "Tipo de paso",
              requerido: true,
              opciones: tiposDePaso,
              sinOpcionInicial: true,
              respuesta: pasoEditando.actividadPasoTipoId
            },
            {
              nombre: "aceptaAdministrador",
              tipoitemformularioid: 6,
              etiqueta: "Acepta la asignación de un administrador",
              respuesta: pasoEditando.aceptaAdministrador
            },
            {
              nombre: "inicial",
              tipoitemformularioid: 6,
              etiqueta: "Es el primer paso de la actividad",
              respuesta: pasoEditando.ref === inicialRef
            },
            {
              nombre: "aprueba",
              tipoitemformularioid: 12,
              etiqueta: "Si se aprueba, va a",
              opciones: destinos,
              respuesta: pasoEditando.aprueba
            },
            {
              nombre: "rechaza",
              tipoitemformularioid: 12,
              etiqueta: "Si se rechaza, va a",
              opciones: destinos,
              respuesta: pasoEditando.rechaza
            }
          ]
        }
      ],
      botones: [{ etiqueta: "Aceptar", callback: aceptarPaso, validar: true }]
    };
    // Se arma al abrir el sidebar: mientras se edita, los valores los lleva
    // el propio Formulario.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const itemsDelMenu = [
    {
      label: "Editar",
      icon: "pi pi-pencil",
      command: () => {
        const paso = pasos.find((p) => p.ref === pasoDelMenu?.ref);
        if (paso) abrirEditarPaso(paso);
      }
    },
    { separator: true },
    {
      label: "Eliminar",
      icon: "pi pi-trash",
      className: "p-menuitem-danger",
      command: () => {
        const paso = pasos.find((p) => p.ref === pasoDelMenu?.ref);
        if (!paso) return;
        confirmDialog({
          message: `¿Eliminar "${paso.nombre}" del flujo?`,
          header: "Confirmar eliminación",
          icon: "pi pi-exclamation-triangle",
          acceptLabel: "Eliminar",
          rejectLabel: "Cancelar",
          acceptClassName: "p-button-danger",
          accept: () => eliminarPaso(paso)
        });
      }
    }
  ];

  return (
    <>
      <Menu model={itemsDelMenu} popup ref={menuRef} />

      <Sidebar
        visible={!!pasoEditando}
        onHide={() => setPasoEditando(null)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>{pasoEditando?.esNuevo ? "Nuevo paso" : "Editar paso"}</Titulo>
        {pasoEditando && configDelPaso && (
          <Formulario key={formKey} config={configDelPaso} />
        )}
      </Sidebar>

      <Titulo
        botones={
          flujo && (
            <>
              {hayCambios && (
                <>
                  <Boton
                    transparente
                    confirmacion="¿Descartar los cambios sin guardar?"
                    callback={() => recibir(flujo)}
                  >
                    Descartar cambios
                  </Boton>
                  <Boton
                    confirmacion="¿Guardar los cambios de los pasos y el flujo?"
                    callback={guardar}
                    loader={guardando}
                  >
                    Guardar cambios
                  </Boton>
                </>
              )}
              <Boton icono negro callback={abrirNuevoPaso}>
                <i className="pi pi-plus" />
              </Boton>
            </>
          )
        }
      >
        Flujo
      </Titulo>

      {!flujo && <Skeleton width="100%" height="8rem" />}

      {flujo && (
        <>
          {!hayCambios &&
            flujo.errores.map((error) => (
              <p key={error} css={{ color: "var(--rojo)", marginTop: 0 }}>
                {error}
              </p>
            ))}

          {pasos.length === 0 ? (
            <p css={{ color: "var(--grisTextos)" }}>
              Todavía no hay pasos configurados.
            </p>
          ) : (
            <DiagramaFlujo
              pasos={pasosDelDiagrama}
              transiciones={transicionesDelDiagrama}
              pasoInicialId={idDiagrama.get(inicialRef) ?? null}
              onMenu={(paso, evento) => {
                setPasoDelMenu(paso);
                menuRef.current.toggle(evento);
              }}
            />
          )}
        </>
      )}
    </>
  );
}
